import { memo, useCallback, useRef, useState } from 'react'
import { supabase } from '../../../lib/supabase'
import {
  PRIORITY_BADGE,
  consignedStateUpdate,
  deriveKanbanFromInput,
  purchasedStateUpdate,
} from '../../../lib/replenishment'
import { painelBucket, PAINEL_BUCKET_META, fillPct } from '../../../lib/painelBucket'
import { useToast } from '../../../hooks/useToast'
import type { KanbanStatusOp, ProductRow } from '../../../types'

// ── helpers de formato ────────────────────────────────────────────────────────
const fmtNum = (n: number, d = 0) =>
  n.toLocaleString('pt-BR', { minimumFractionDigits: d, maximumFractionDigits: d })
const fmtMoney = (n: number) =>
  n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const fmtDate = (iso: string | null) => {
  if (!iso) return '—'
  const d = new Date(iso)
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}`
}

// Selo da curva ABC (identificação visual rápida). Convenção do app: A=azul,
// B=âmbar, C=neutro. dual-theme.
const ABC_BADGE: Record<'A' | 'B' | 'C', string> = {
  A: 'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300',
  B: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',
  C: 'bg-sunken text-ink-muted border border-line',
}

function remoteValue(r: ProductRow): string {
  if (r.is_consigned) return r.approved_qty > 0 ? String(r.approved_qty) : ''
  return r.approval_input ?? (r.approved_qty > 0 ? String(r.approved_qty) : '')
}

interface Props {
  row: ProductRow
  onInactivate: (id: string, name: string) => Promise<void>
  inactivating: boolean
}

const MobileReplenishmentCard = memo(function MobileReplenishmentCard({ row: r, onInactivate, inactivating }: Props) {
  const initialRemote = remoteValue(r)
  const [draft, setDraft] = useState(initialRemote)
  const [saving, setSaving] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const debounceRef = useRef<number | null>(null)
  const { notify } = useToast()

  // Bucket semântico (pílula + barra) a partir do estado real da linha.
  const bucket = painelBucket({
    priority: r.priority, kanban_status: r.kanban_status, current_stock: r.current_stock, em_risco: r.em_risco,
  })
  const meta = PAINEL_BUCKET_META[bucket]
  const isRuptura = bucket === 'ruptura'
  const sugerido = Math.floor(r.total_suggestion)

  const commit = useCallback(async (raw: string) => {
    const { status, numericQty } = deriveKanbanFromInput(raw)
    const normalized = (raw ?? '').trim().toUpperCase()
    const remoteNow = remoteValue(r)
    if (normalized === remoteNow.trim().toUpperCase()) return
    setSaving(true)
    const update = r.is_consigned
      ? { approval_input: null, approved_qty: numericQty, kanban_status: 'Consignado' as KanbanStatusOp }
      : { approval_input: normalized || null, approved_qty: numericQty, kanban_status: status }
    const { error } = await supabase.from('products').update(update).eq('id', r.id)
    setSaving(false)
    if (error) { setDraft(remoteNow); notify('error', `Erro: ${error.message}`) }
  }, [r, notify])

  function adjustQty(delta: number) {
    const current = parseInt(draft) || 0
    const next = Math.max(0, current + delta).toString()
    setDraft(next)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = window.setTimeout(() => commit(next), 600)
  }

  function handleQtyChange(value: string) {
    setDraft(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = window.setTimeout(() => commit(value), 600)
  }

  // "Aprovar compra": aprova a quantidade SUGERIDA direto (1 toque). Ajuste fino
  // fica no stepper dentro de "Detalhes".
  async function aprovarCompra() {
    if (sugerido <= 0) { notify('error', 'Sem quantidade sugerida para este item.'); return }
    setSaving(true)
    const { error } = await supabase.from('products').update({
      approval_input: String(sugerido), approved_qty: sugerido, kanban_status: 'Compra Aprovada' as KanbanStatusOp,
    }).eq('id', r.id)
    setSaving(false)
    if (error) notify('error', `Erro: ${error.message}`)
    else { setDraft(String(sugerido)); notify('success', `${r.name}: aprovado (${sugerido}).`) }
  }

  async function toggleConsigned(next: boolean) {
    if (next === r.is_consigned) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const update = next ? consignedStateUpdate() : purchasedStateUpdate()
    const { error } = await supabase.from('products').update(update).eq('id', r.id)
    if (error) notify('error', `Erro: ${error.message}`)
    else setDraft('')
  }

  return (
    <div className="rounded-xl border border-line bg-surface overflow-hidden no-tap-highlight">
      {/* Corpo tocável — toque no card abre/fecha os detalhes */}
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        aria-expanded={expanded}
        className="w-full text-left px-3.5 pt-3.5 pb-2 active:bg-sunken/40 touch-action-manip"
      >
        {/* Nome + pílula de status */}
        <div className="flex items-start justify-between gap-2">
          <p className="text-ink text-sm font-semibold leading-snug line-clamp-2 flex-1 min-w-0">{r.name}</p>
          <span className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${meta.badge}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
            {meta.label}
          </span>
        </div>
        {/* SKU + semáforo de prioridade (cores canônicas do motor; chip no mesmo
            tamanho da pílula de status — px-2.5 py-1 text-[11px]) */}
        <div className="flex items-center justify-between gap-2 mt-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <p className="text-[11px] text-ink-faint font-mono truncate">{r.sku}</p>
            {r.abc_curve && (
              <span
                className={`shrink-0 inline-flex items-center justify-center w-[18px] h-[18px] rounded text-[10px] font-bold leading-none ${ABC_BADGE[r.abc_curve]}`}
                title={`Curva ${r.abc_curve}`}
              >
                {r.abc_curve}
              </span>
            )}
          </div>
          <span className={`shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${PRIORITY_BADGE[r.priority]}`}>
            {r.priority}
          </span>
        </div>

        {/* Barra de progresso (estoque / ponto de reposição) */}
        <div className="mt-2.5 h-1.5 rounded-full bg-sunken overflow-hidden">
          <div className={`h-full rounded-full ${meta.bar} transition-all`} style={{ width: `${fillPct(r.current_stock, r.reorder_point)}%` }} />
        </div>

        {/* Métricas: Estoque · Mínimo · Sugerido · Fornecedor */}
        <div className="mt-3 flex items-end justify-between gap-2">
          <div className="grid grid-cols-3 gap-3 flex-1 min-w-0">
            <Metric label="Estoque" value={`${fmtNum(r.current_stock)} un`} tone={isRuptura ? 'danger' : 'ink'} />
            <Metric label="Mínimo" value={`${fmtNum(r.reorder_point)} un`} tone="ink" />
            <Metric label="Sugerido" value={sugerido > 0 ? `${fmtNum(sugerido)} un` : '—'} tone={sugerido > 0 ? 'accent' : 'faint'} />
          </div>
          <div className="text-right shrink-0 max-w-[38%]">
            <p className="text-[10px] text-ink-faint uppercase tracking-wider">Fornecedor</p>
            <p className="text-xs font-semibold text-ink truncate" title={r.suppliers?.name ?? ''}>{r.suppliers?.name ?? '—'}</p>
          </div>
        </div>

        {/* Indicador de toque (abre/fecha os detalhes) */}
        <div className="mt-2 flex items-center justify-center gap-1 text-ink-faint">
          <span className="text-[10px] font-medium uppercase tracking-wide">{expanded ? 'Menos' : 'Detalhes'}</span>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`}>
            <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </button>

      {/* Painel "Detalhes" — ajuste fino + dados + ações secundárias */}
      {expanded && (
        <div className="px-3.5 pb-3.5 pt-1 space-y-3 border-t border-line bg-sunken/30">
          {/* Ajuste fino de quantidade (stepper) */}
          <div>
            <p className="text-[10px] text-ink-faint uppercase tracking-wider mb-1.5">Ajustar quantidade</p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => adjustQty(-1)}
                disabled={saving}
                className="w-10 h-10 rounded-lg bg-sunken active:bg-line text-ink text-xl font-bold flex items-center justify-center touch-action-manip disabled:opacity-50"
              >−</button>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                value={draft}
                onChange={e => handleQtyChange(e.target.value)}
                onBlur={e => commit(e.target.value)}
                placeholder={String(sugerido)}
                className={`flex-1 h-10 bg-paper border rounded-lg px-2 text-center text-ink text-base font-bold tabular-nums ${saving ? 'border-yellow-400/60' : 'border-line'}`}
              />
              <button
                onClick={() => adjustQty(1)}
                disabled={saving}
                className="w-10 h-10 rounded-lg bg-sunken active:bg-line text-ink text-xl font-bold flex items-center justify-center touch-action-manip disabled:opacity-50"
              >+</button>
            </div>
          </div>

          {/* Grade de detalhes */}
          <div className="grid grid-cols-2 gap-2 text-sm">
            {[
              ['Média mensal', fmtNum(r.monthly_avg, 1)],
              ['Média diária', fmtNum(r.daily_avg, 2)],
              ['Cobertura', `${fmtNum(r.coverage_days)}d`],
              ['Lead time', r.suppliers?.lead_time_days ? `${r.suppliers.lead_time_days}d` : '—'],
              ['Ponto repos.', fmtNum(r.reorder_point, 1)],
              ['Gap compra', fmtNum(r.gap_to_buy, 1)],
              ['Custo unit.', fmtMoney(r.cost)],
              ['Última venda', fmtDate(r.last_sale_at)],
            ].map(([label, value]) => (
              <div key={label} className="bg-paper rounded-lg px-2.5 py-1.5 border border-line">
                <p className="text-[10px] text-ink-faint uppercase tracking-wider">{label}</p>
                <p className="text-ink font-semibold tabular-nums text-xs mt-0.5">{value}</p>
              </div>
            ))}
          </div>

          {/* Prioridade + encalhe + toggle consignado */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${PRIORITY_BADGE[r.priority]}`}>
              {r.priority}
            </span>
            {r.encalhe && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-warn/15 text-[#9a5a28] dark:bg-warn/20 dark:text-amber-300 border border-warn/40">
                ⚠ Encalhe
              </span>
            )}
            <div className="ml-auto inline-flex rounded-lg border border-line overflow-hidden text-[11px] font-bold">
              <button
                onClick={() => toggleConsigned(true)}
                disabled={r.is_consigned}
                className={`px-2.5 py-1.5 touch-action-manip transition-colors ${r.is_consigned ? 'bg-yellow-500 text-gray-900' : 'bg-sunken text-ink-muted'}`}
              >CONSIG</button>
              <button
                onClick={() => toggleConsigned(false)}
                disabled={!r.is_consigned}
                className={`px-2.5 py-1.5 border-l border-line touch-action-manip transition-colors ${!r.is_consigned ? 'bg-gray-200 text-gray-900' : 'bg-sunken text-ink-muted'}`}
              >COMP</button>
            </div>
          </div>

          {/* Ação: inativar */}
          <button
            onClick={() => onInactivate(r.id, r.name)}
            disabled={inactivating}
            className="w-full py-2 rounded-lg border border-line text-ink-muted text-xs active:bg-sunken touch-action-manip disabled:opacity-40"
          >
            {inactivating ? 'Inativando...' : 'Inativar produto'}
          </button>
        </div>
      )}

      {/* Ação primária — sempre no rodapé */}
      <div className={`px-3.5 pb-3.5 pt-2.5 ${expanded ? 'border-t border-line' : ''}`}>
        <button
          onClick={aprovarCompra}
          disabled={saving || sugerido <= 0}
          className="w-full h-10 rounded-lg bg-accent text-brand-900 text-sm font-bold active:brightness-95 touch-action-manip disabled:opacity-40"
        >
          {saving ? '...' : 'Aprovar compra'}
        </button>
      </div>
    </div>
  )
})

function Metric({ label, value, tone }: { label: string; value: string; tone: 'ink' | 'danger' | 'accent' | 'faint' }) {
  const color = tone === 'danger' ? 'text-danger' : tone === 'accent' ? 'text-accent' : tone === 'faint' ? 'text-ink-faint' : 'text-ink'
  return (
    <div className="min-w-0">
      <p className="text-[10px] text-ink-faint uppercase tracking-wider truncate">{label}</p>
      <p className={`text-sm font-bold tabular-nums truncate ${color}`}>{value}</p>
    </div>
  )
}

export default MobileReplenishmentCard
